import React, { useState } from "react";
import { jobsAPI } from "../api/client";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { mayPerformWithRole } from "../utils/userAccess";
import LoadingButton from "../components/LoadingButton";
import "./CreateJob.css";

/* ================= MANDATORY FIELD CONFIGURATION =================
   Every field on the Add Job form is mandatory. This single map drives the
   validation loop, the generated error messages and the label ordering, so
   the form can never be submitted with a missing / blank value. */
export const JOB_FORM_FIELDS = {
  title: "Job Title",
  client_name: "Client Name",
  positions: "Number of Positions",
  status: "Status",
  priority: "Priority",
  job_date: "Job Date",
  experience_required: "Experience Required",
  location: "Location",
  ctc_range: "CTC Range",
  department: "Department",
  notice_period: "Notice Period",
  skills: "Skills",
  job_description: "Job Description",
  recruit_manager: "Recruitor",
  hiring_manager_name: "Hiring Manager Name",
  hiring_manager_email: "Hiring Manager Email",
  request_by: "Request By",
  created_by: "Created By",
  requirement_approx_value: "Requirement Approx Value",
};

export const JOB_FORM_FIELD_ORDER = Object.keys(JOB_FORM_FIELDS);

const VALID_STATUSES = ["open", "closed", "hold"];
const VALID_PRIORITIES = ["Priority 1", "Priority 2", "Priority 3"];
const VALID_DEPARTMENTS = ["ICDS", "Embedded"];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Placeholder entries such as "Select Department" / "Select..." are not real selections. */
const isPlaceholderOption = (value) => {
  const text = String(value ?? "").trim();
  return !text || /^select\b/i.test(text);
};

const isValidDateString = (value) => {
  const text = String(value ?? "").trim();
  if (!DATE_PATTERN.test(text)) return false;
  const [year, month, day] = text.split("-").map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
};

/** Trim every submitted value so whitespace-only input is treated as empty. */
export const normalizeJobForm = (values = {}) =>
  JOB_FORM_FIELD_ORDER.reduce((acc, key) => {
    const raw = values[key];
    acc[key] = raw === null || raw === undefined ? "" : String(raw).trim();
    return acc;
  }, {});

/**
 * Validate the whole Add Job payload.
 * @returns {{ data: object, errors: Record<string,string>, isValid: boolean }}
 */
export const validateJobForm = (values = {}) => {
  const data = normalizeJobForm(values);
  const errors = {};

  // 1) Mandatory check - whitespace-only values are already trimmed to "".
  JOB_FORM_FIELD_ORDER.forEach((key) => {
    if (data[key] === "") {
      errors[key] = `${JOB_FORM_FIELDS[key]} is required`;
    }
  });

  // 2) Dropdowns must hold a real option, never a "Select..." placeholder.
  if (data.department) {
    if (isPlaceholderOption(data.department)) {
      errors.department = "Please select a department";
    } else if (!VALID_DEPARTMENTS.includes(data.department)) {
      errors.department = "Please select a valid department";
    }
  }
  if (data.status && !VALID_STATUSES.includes(data.status)) {
    errors.status = "Please select a valid status";
  }
  if (data.priority && !VALID_PRIORITIES.includes(data.priority)) {
    errors.priority = "Please select a valid priority";
  }

  // 3) Field level format rules for the typed / numeric / date inputs.
  if (data.positions && (!/^\d+$/.test(data.positions) || Number(data.positions) < 1)) {
    errors.positions = "Number of positions must be a whole number of 1 or more";
  }
  if (
    data.requirement_approx_value &&
    (!Number.isFinite(Number(data.requirement_approx_value)) ||
      Number(data.requirement_approx_value) < 0)
  ) {
    errors.requirement_approx_value = "Requirement Approx Value must be a number of 0 or more";
  }
  if (data.job_date && !isValidDateString(data.job_date)) {
    errors.job_date = "Please enter a valid job date";
  }
  if (data.hiring_manager_email && !EMAIL_PATTERN.test(data.hiring_manager_email)) {
    errors.hiring_manager_email = "Please enter a valid email address";
  }

  return { data, errors, isValid: Object.keys(errors).length === 0 };
};

/** Pull a human readable message out of an API error payload. */
const extractApiError = (err) => {
  const payload = err?.response?.data;
  if (!payload) return "Failed to create job";
  if (payload.fields && typeof payload.fields === "object") {
    const messages = Object.values(payload.fields).filter(Boolean);
    if (messages.length) return messages.join(", ");
  }
  if (typeof payload.error === "string") return payload.error;
  if (payload.error?.message) {
    const details = payload.error.details;
    if (Array.isArray(details) && details.length) {
      return details.map((d) => d.msg).filter(Boolean).join(", ") || payload.error.message;
    }
    return payload.error.message;
  }
  if (typeof payload.message === "string") return payload.message;
  return "Failed to create job";
};

/**
 * Map server-side validation errors back onto the form fields. The route
 * middleware returns express-validator `details`; the controller guard returns
 * a `fields` map. Both shapes are supported.
 */
const errorsFromApiPayload = (payload) => {
  const applyEntry = (acc, key, msg) => {
    if (key && JOB_FORM_FIELDS[key] && !acc[key]) {
      acc[key] = msg || `${JOB_FORM_FIELDS[key]} is required`;
    }
  };

  const details = payload?.error?.details;
  if (Array.isArray(details)) {
    return details.reduce((acc, item) => {
      applyEntry(acc, item?.path, item?.msg);
      return acc;
    }, {});
  }

  if (payload?.fields && typeof payload.fields === "object") {
    return Object.entries(payload.fields).reduce((acc, [key, msg]) => {
      applyEntry(acc, key, msg);
      return acc;
    }, {});
  }

  return {};
};

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p className="field-error" id={id} role="alert">
      {message}
    </p>
  );
}

export default function CreateJob() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    client_name: "",
    positions: "",
    status: "open",
    priority: "",
    job_date: new Date().toISOString().slice(0, 10),
    experience_required: "",
    location: "",
    ctc_range: "",
    department: "",
    notice_period: "",
    skills: "",
    job_description: "",
    recruit_manager: "",
    hiring_manager_name: "",
    hiring_manager_email: "",
    request_by: "",
    created_by: "",
    requirement_approx_value: ""
  });

  if (!mayPerformWithRole(user?.role, "job", "add", "job.manage")) {
    return <Navigate to="/jobs" replace />;
  }

  /** Clear a field's error as soon as the user edits it. */
  const clearFieldError = (name) => {
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    clearFieldError(name);

    if (name === "positions") {
      setFormData((prev) => ({ ...prev, positions: value === "" ? "" : parseInt(value, 10) }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /** Scroll to and focus the first field that still has an error. */
  const focusFirstError = (validationErrors) => {
    const firstField = JOB_FORM_FIELD_ORDER.find((key) => validationErrors[key]);
    if (!firstField) return;
    const element = document.getElementById(firstField);
    if (!element) return;
    element.focus();
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { data, errors: validationErrors, isValid } = validateJobForm(formData);

    if (!isValid) {
      setErrors(validationErrors);
      setSubmitted(true);
      toast.error("Please complete all required fields before creating the job.");
      focusFirstError(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      await jobsAPI.create(data);
      toast.success("Job created successfully");
      navigate("/jobs");
    } catch (err) {
      console.error(err);
      // Surface any server-side field errors on the matching inputs.
      const apiFieldErrors = errorsFromApiPayload(err?.response?.data);
      if (Object.keys(apiFieldErrors).length) {
        setErrors(apiFieldErrors);
        setSubmitted(true);
        focusFirstError(apiFieldErrors);
      }
      toast.error(extractApiError(err));
    } finally {
      setLoading(false);
    }
  };

  /** Class helper so invalid fields get the red border. */
  const fieldClass = (name) => (errors[name] ? "field-invalid" : undefined);

  const describedBy = (name) => (errors[name] ? `${name}-error` : undefined);

  return (
    <div className="create-job-page">
      <div className="create-job-header">
        <h1>Create Job</h1>
        <p>Fill in the details below to post a new job opening.</p>
      </div>

      <div className="create-job-card">
        <form onSubmit={handleSubmit} className="create-job-form" noValidate>

          {submitted && Object.keys(errors).length > 0 && (
            <div className="form-error-summary" role="alert">
              <strong>
                {Object.keys(errors).length} field
                {Object.keys(errors).length === 1 ? "" : "s"} need attention
              </strong>
              <span>All fields are mandatory before a job can be created.</span>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="title">Job Title <span className="required">*</span></label>
              <input
                id="title"
                type="text"
                name="title"
                placeholder="e.g. Senior React Developer"
                value={formData.title}
                onChange={handleChange}
                aria-invalid={Boolean(errors.title)}
                aria-describedby={describedBy("title")}
                className={fieldClass("title")}
                required
              />
              <FieldError id="title-error" message={errors.title} />
            </div>
            <div className="form-group">
              <label htmlFor="client_name">Client Name <span className="required">*</span></label>
              <input
                id="client_name"
                type="text"
                name="client_name"
                placeholder="e.g. Acme Corp"
                value={formData.client_name}
                onChange={handleChange}
                aria-invalid={Boolean(errors.client_name)}
                aria-describedby={describedBy("client_name")}
                className={fieldClass("client_name")}
                required
              />
              <FieldError id="client_name-error" message={errors.client_name} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="positions">Number of Positions <span className="required">*</span></label>
                <input
                  id="positions"
                  type="number"
                  min="1"
                  step="1"
                  name="positions"
                  placeholder="e.g. 2"
                  value={formData.positions}
                  onChange={handleChange}
                  aria-invalid={Boolean(errors.positions)}
                  aria-describedby={describedBy("positions")}
                  className={`no-spinner${errors.positions ? " field-invalid" : ""}`}
                  required
                />
              <FieldError id="positions-error" message={errors.positions} />
            </div>
            <div className="form-group">
              <label htmlFor="status">Status <span className="required">*</span></label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                aria-invalid={Boolean(errors.status)}
                aria-describedby={describedBy("status")}
                className={fieldClass("status")}
                required
              >
                <option value="">Select Status</option>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="hold">On Hold</option>
              </select>
              <FieldError id="status-error" message={errors.status} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="priority">Priority <span className="required">*</span></label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                aria-invalid={Boolean(errors.priority)}
                aria-describedby={describedBy("priority")}
                className={fieldClass("priority")}
                required
              >
                <option value="">Select Priority</option>
                <option value="Priority 1">Priority 1</option>
                <option value="Priority 2">Priority 2</option>
                <option value="Priority 3">Priority 3</option>
              </select>
              <FieldError id="priority-error" message={errors.priority} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="recruit_manager">Recruitor <span className="required">*</span></label>
              <input
                id="recruit_manager"
                type="text"
                name="recruit_manager"
                placeholder="e.g. John Doe"
                value={formData.recruit_manager}
                onChange={handleChange}
                aria-invalid={Boolean(errors.recruit_manager)}
                aria-describedby={describedBy("recruit_manager")}
                className={fieldClass("recruit_manager")}
                required
              />
              <FieldError id="recruit_manager-error" message={errors.recruit_manager} />
            </div>
            <div className="form-group">
              <label htmlFor="job_date">Job Date <span className="required">*</span></label>
              <input
                id="job_date"
                type="date"
                name="job_date"
                value={formData.job_date}
                onChange={handleChange}
                aria-invalid={Boolean(errors.job_date)}
                aria-describedby={describedBy("job_date")}
                className={fieldClass("job_date")}
                required
              />
              <FieldError id="job_date-error" message={errors.job_date} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="hiring_manager_name">Hiring Manager Name <span className="required">*</span></label>
              <input
                id="hiring_manager_name"
                type="text"
                name="hiring_manager_name"
                value={formData.hiring_manager_name}
                onChange={handleChange}
                placeholder="e.g. Priya Sharma"
                aria-invalid={Boolean(errors.hiring_manager_name)}
                aria-describedby={describedBy("hiring_manager_name")}
                className={fieldClass("hiring_manager_name")}
                required
              />
              <FieldError id="hiring_manager_name-error" message={errors.hiring_manager_name} />
            </div>
            <div className="form-group">
              <label htmlFor="request_by">Request By <span className="required">*</span></label>
              <input
                id="request_by"
                type="text"
                name="request_by"
                value={formData.request_by}
                onChange={handleChange}
                placeholder="e.g. Who requested the requirement"
                aria-invalid={Boolean(errors.request_by)}
                aria-describedby={describedBy("request_by")}
                className={fieldClass("request_by")}
                required
              />
              <FieldError id="request_by-error" message={errors.request_by} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="hiring_manager_email">Hiring Manager Email <span className="required">*</span></label>
              <input
                id="hiring_manager_email"
                type="email"
                name="hiring_manager_email"
                value={formData.hiring_manager_email}
                onChange={handleChange}
                placeholder="e.g. priya@company.com"
                aria-invalid={Boolean(errors.hiring_manager_email)}
                aria-describedby={describedBy("hiring_manager_email")}
                className={fieldClass("hiring_manager_email")}
                required
              />
              <FieldError id="hiring_manager_email-error" message={errors.hiring_manager_email} />
            </div>
            <div className="form-group">
              <label htmlFor="created_by">Created By <span className="required">*</span></label>
              <input
                id="created_by"
                type="text"
                name="created_by"
                value={formData.created_by}
                onChange={handleChange}
                placeholder="e.g. Who created this job"
                aria-invalid={Boolean(errors.created_by)}
                aria-describedby={describedBy("created_by")}
                className={fieldClass("created_by")}
                required
              />
              <FieldError id="created_by-error" message={errors.created_by} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="notice_period">Notice Period <span className="required">*</span></label>
              <input
                id="notice_period"
                type="text"
                name="notice_period"
                placeholder="e.g. 30 days, Immediate"
                value={formData.notice_period}
                onChange={handleChange}
                aria-invalid={Boolean(errors.notice_period)}
                aria-describedby={describedBy("notice_period")}
                className={fieldClass("notice_period")}
                required
              />
              <FieldError id="notice_period-error" message={errors.notice_period} />
            </div>
            <div className="form-group">
              <label htmlFor="requirement_approx_value">Requirement Approx Value <span className="required">*</span></label>
              <input
                id="requirement_approx_value"
                type="number"
                min="0"
                step="0.01"
                name="requirement_approx_value"
                placeholder="e.g. 2500000"
                value={formData.requirement_approx_value}
                onChange={handleChange}
                aria-invalid={Boolean(errors.requirement_approx_value)}
                aria-describedby={describedBy("requirement_approx_value")}
                className={`no-spinner${errors.requirement_approx_value ? " field-invalid" : ""}`}
                required
              />
              <FieldError id="requirement_approx_value-error" message={errors.requirement_approx_value} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="experience_required">Experience Required <span className="required">*</span></label>
              <input
                id="experience_required"
                type="text"
                name="experience_required"
                placeholder="e.g. 3-5 years"
                value={formData.experience_required}
                onChange={handleChange}
                aria-invalid={Boolean(errors.experience_required)}
                aria-describedby={describedBy("experience_required")}
                className={fieldClass("experience_required")}
                required
              />
              <FieldError id="experience_required-error" message={errors.experience_required} />
            </div>
            <div className="form-group">
              <label htmlFor="location">Location <span className="required">*</span></label>
              <input
                id="location"
                type="text"
                name="location"
                placeholder="e.g. Bangalore, Remote"
                value={formData.location}
                onChange={handleChange}
                aria-invalid={Boolean(errors.location)}
                aria-describedby={describedBy("location")}
                className={fieldClass("location")}
                required
              />
              <FieldError id="location-error" message={errors.location} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="ctc_range">CTC Range <span className="required">*</span></label>
              <input
                id="ctc_range"
                type="text"
                name="ctc_range"
                placeholder="e.g. 10-15 LPA"
                value={formData.ctc_range}
                onChange={handleChange}
                aria-invalid={Boolean(errors.ctc_range)}
                aria-describedby={describedBy("ctc_range")}
                className={fieldClass("ctc_range")}
                required
              />
              <FieldError id="ctc_range-error" message={errors.ctc_range} />
            </div>
            <div className="form-group">
              <label htmlFor="department">Department <span className="required">*</span></label>
              <select
                id="department"
                name="department"
                value={formData.department}
                onChange={handleChange}
                aria-invalid={Boolean(errors.department)}
                aria-describedby={describedBy("department")}
                className={fieldClass("department")}
                required
              >
                <option value="">Select Department</option>
                <option value="ICDS">ICDS</option>
                <option value="Embedded">Embedded</option>
              </select>
              <FieldError id="department-error" message={errors.department} />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="skills">Skills <span className="required">*</span></label>
            <textarea
              id="skills"
              name="skills"
              placeholder="e.g. React, Node.js, PostgreSQL"
              rows="3"
              value={formData.skills}
              onChange={handleChange}
              aria-invalid={Boolean(errors.skills)}
              aria-describedby={describedBy("skills")}
              className={fieldClass("skills")}
              required
            />
            <FieldError id="skills-error" message={errors.skills} />
          </div>

          <div className="form-group">
            <label htmlFor="job_description">Job Description <span className="required">*</span></label>
            <textarea
              id="job_description"
              name="job_description"
              placeholder="Describe the role, responsibilities, and requirements..."
              rows="5"
              value={formData.job_description}
              onChange={handleChange}
              aria-invalid={Boolean(errors.job_description)}
              aria-describedby={describedBy("job_description")}
              className={fieldClass("job_description")}
              required
            />
            <FieldError id="job_description-error" message={errors.job_description} />
          </div>

          <div className="create-job-actions">
            <button type="button" className="btn-cancel" onClick={() => navigate('/jobs')}>
              Cancel
            </button>
            <LoadingButton type="submit" className="btn-submit" disabled={loading} loading={loading} loadingText="Creating...">
              Create Job
            </LoadingButton>
          </div>

        </form>
      </div>
    </div>
  );
}
